/**
 * quizValidation — guards the play area against malformed AI-generated questions.
 *
 * The tutor model (and other LLM exercise generators) occasionally emits a
 * question that is either missing a field its renderer dereferences directly, or
 * internally inconsistent in a way that makes the exercise *unwinnable* — e.g. a
 * `word-order` whose tiles aren't a permutation of the target, or a
 * `fill-in-blank-tiles` whose answer isn't available in the word bank. Both
 * paths hurt the learner: the first white-screens the Tutor page ("Cannot read
 * properties of undefined (reading 'includes')"), the second marks a correct
 * answer wrong and tanks the concept's spaced-repetition schedule.
 *
 * We validate each question against (a) the minimum shape its renderer requires
 * and (b) the consistency the grader needs for the exercise to be winnable, then
 * silently drop the ones that fail. A single bad question can neither crash the
 * session nor mislead the learner.
 *
 * The cosmetic `prompt` heading is deliberately NOT required: the model often
 * omits it (e.g. on `article-selection`, where the noun + article buttons are
 * self-explanatory), and the renderer treats it as plain text — a missing prompt
 * shows a blank heading, it never crashes. Dropping an otherwise winnable
 * question over a missing heading would starve the learner of valid exercises, so
 * instead `sanitizeQuizQuestions` backfills a per-type default prompt.
 */

import type { QuizQuestion } from '@/types/quiz';

/** The blank marker used in fill-in-blank templates. */
const GAP = '___';

const isNonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

const isStringArray = (v: unknown): v is string[] =>
  Array.isArray(v) && v.length > 0 && v.every((x) => typeof x === 'string');

/** A non-negative integer usable as an index into an array of `length`. */
const isIndexInRange = (v: unknown, length: number): boolean =>
  typeof v === 'number' && Number.isInteger(v) && v >= 0 && v < length;

/** True when `a` and `b` contain the same strings with the same multiplicity. */
const sameMultiset = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((x, i) => x === sb[i]);
};

/** True when every string in `needles` exists in `haystack` with enough copies. */
const isSubMultiset = (needles: string[], haystack: string[]): boolean => {
  const counts = new Map<string, number>();
  for (const h of haystack) counts.set(h, (counts.get(h) ?? 0) + 1);
  for (const n of needles) {
    const remaining = counts.get(n) ?? 0;
    if (remaining === 0) return false;
    counts.set(n, remaining - 1);
  }
  return true;
};

/** Number of `___` blank markers in a sentence template. */
const gapCount = (template: string): number => template.split(GAP).length - 1;

/** A `[leftIndex, rightIndex]` tuple of two non-negative integers. */
const isPair = (p: unknown): p is [number, number] =>
  Array.isArray(p) &&
  p.length === 2 &&
  Number.isInteger(p[0]) &&
  Number.isInteger(p[1]) &&
  p[0] >= 0 &&
  p[1] >= 0;

/** Generic heading used when the model omits the cosmetic `prompt` field. */
const DEFAULT_PROMPTS: Record<QuizQuestion['type'], string> = {
  'multiple-choice': 'Choose the correct answer.',
  'fill-in-blank': 'Fill in the blank.',
  'article-selection': 'Choose the correct article.',
  'word-order': 'Arrange the words in the correct order.',
  'fill-in-blank-tiles': 'Drag the tiles into the correct blanks.',
  'match-up': 'Match each item to its translation.',
  'minimal-pair': 'Which word did you hear?',
};

/**
 * True when `q` is renderable and winnable for its type. Requires a stable `id`
 * (used as the React key / answer map key) and the per-type fields the renderer
 * and grader dereference. The cosmetic `prompt` heading is intentionally NOT
 * required — `sanitizeQuizQuestions` backfills a default for any survivor that
 * lacks one. Optional/forgiving fields (e.g. article-selection's
 * `targetLanguage`, which the renderer already falls back on) are likewise not
 * required here.
 */
export function isValidQuizQuestion(q: unknown): q is QuizQuestion {
  if (!q || typeof q !== 'object') return false;
  const o = q as Record<string, unknown>;
  if (!isNonEmptyString(o.id)) return false;

  switch (o.type) {
    case 'multiple-choice': {
      // Renderer reads stem.includes + options.map; grader reads
      // options[correctIndex], so the index must point at a real option.
      const { stem, options, correctIndex } = o;
      return (
        isNonEmptyString(stem) &&
        isStringArray(options) &&
        isIndexInRange(correctIndex, options.length)
      );
    }

    case 'fill-in-blank':
      // Grader fuzzy-matches against acceptedAnswers; the first is shown as THE answer.
      return isNonEmptyString(o.sentenceTemplate) && isStringArray(o.acceptedAnswers);

    case 'article-selection':
      // targetLanguage is optional — the renderer falls back to der/die/das.
      return isNonEmptyString(o.noun) && isNonEmptyString(o.correctArticle);

    case 'word-order': {
      // The player rearranges scrambledTiles to equal correctOrder, so the tiles
      // must be a permutation of the target or the exercise can never be solved.
      const { scrambledTiles, correctOrder } = o;
      return (
        isStringArray(scrambledTiles) &&
        isStringArray(correctOrder) &&
        correctOrder.length >= 2 &&
        sameMultiset(scrambledTiles, correctOrder)
      );
    }

    case 'fill-in-blank-tiles': {
      // One tile per blank, in order: the blank count must match the answer
      // count, and every answer tile must be available in the word bank.
      const { sentenceTemplate, wordBank, correctBlanks } = o;
      if (
        !isNonEmptyString(sentenceTemplate) ||
        !isStringArray(wordBank) ||
        !isStringArray(correctBlanks)
      ) {
        return false;
      }
      return (
        gapCount(sentenceTemplate) === correctBlanks.length &&
        isSubMultiset(correctBlanks, wordBank)
      );
    }

    case 'match-up': {
      // The player must pair every left item exactly once; grading compares the
      // full pair set. So correctMatches must cover each left index once and map
      // to valid, distinct right indices.
      const { leftItems, rightItems, correctMatches } = o;
      if (
        !isStringArray(leftItems) ||
        !isStringArray(rightItems) ||
        !Array.isArray(correctMatches) ||
        correctMatches.length !== leftItems.length ||
        !correctMatches.every(isPair)
      ) {
        return false;
      }
      const pairs = correctMatches as [number, number][];
      const lefts = pairs.map(([l]) => l);
      const rights = pairs.map(([, r]) => r);
      const leftsCoverAllOnce =
        new Set(lefts).size === leftItems.length && lefts.every((l) => l < leftItems.length);
      const rightsValidAndDistinct =
        new Set(rights).size === rights.length && rights.every((r) => r < rightItems.length);
      return leftsCoverAllOnce && rightsValidAndDistinct;
    }

    case 'minimal-pair': {
      // Two distinct words as tap targets; the played word must be one of them or
      // the exercise is unwinnable. optionOrder must place each button exactly once.
      const pairArr = Array.isArray(o.pair) ? o.pair : null;
      const ipaArr = Array.isArray(o.ipa) ? o.ipa : null;
      const orderArr = Array.isArray(o.optionOrder) ? o.optionOrder : null;
      const [p0, p1] = pairArr ?? [];
      const [a0, a1] = orderArr ?? [];
      const played = o.playedWord;
      const pairOk =
        pairArr !== null &&
        pairArr.length === 2 &&
        isNonEmptyString(p0) &&
        isNonEmptyString(p1) &&
        p0 !== p1;
      const ipaOk =
        ipaArr !== null &&
        ipaArr.length === 2 &&
        isNonEmptyString(ipaArr[0]) &&
        isNonEmptyString(ipaArr[1]);
      const playedOk = isNonEmptyString(played) && (played === p0 || played === p1);
      const orderOk =
        orderArr !== null &&
        orderArr.length === 2 &&
        typeof a0 === 'number' &&
        Number.isInteger(a0) &&
        typeof a1 === 'number' &&
        Number.isInteger(a1) &&
        ((a0 === 0 && a1 === 1) || (a0 === 1 && a1 === 0));
      return (
        pairOk &&
        ipaOk &&
        playedOk &&
        orderOk &&
        isNonEmptyString(o.contrast) &&
        isNonEmptyString(o.targetLanguage)
      );
    }

    default:
      return false;
  }
}

/**
 * Filter a raw questions payload down to the renderable, winnable questions, and
 * backfill a default `prompt` heading for any survivor that lacks one so every
 * returned question satisfies the `QuizQuestion` contract.
 */
export function sanitizeQuizQuestions(raw: unknown): QuizQuestion[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(isValidQuizQuestion)
    .map((q) => (isNonEmptyString(q.prompt) ? q : { ...q, prompt: DEFAULT_PROMPTS[q.type] }));
}
